import {
  HireUnitType,
  RelocationInclusionType,
  RelocationType,
} from "@/gql/graphql";
import { z } from "zod";

export const connectBelongsTo = z.object({
  connect: z.string().min(1),
});

/**
 * The fields shared by everything stored in the relocations table. Gap rentals
 * build their own schema from these (see gapRentalValidation) — the data
 * structure is shared, the rules are not.
 */
export const relocationFields = z.object({
  available_from_date: z.string(),
  available_to_date: z.string(),
  fixed_latest_departure_date: z.string().nullish(),
  departureOffice: connectBelongsTo,
  deliveryOfficeIds: z
    .array(z.string())
    .min(1, "At least one delivery office is required"),
  supplier: connectBelongsTo,
  vehicle: connectBelongsTo,
  supplierInsuranceOption: connectBelongsTo,
  quantity: z.number().min(1),
  type: z.enum(RelocationType).optional(),
  retail_rate: z.number().min(0).nullable(),
  discount_percent: z.number().int().min(0).max(90).nullable(),
  hire_unit_rate: z.number().min(0),
  hire_unit_type: z.enum(HireUnitType),
  hire_units_allowed: z.number().min(0),
  minimum_hire_units: z.number().min(0),
  extra_hire_units_allowed: z.number().min(0),
  extra_hire_unit_rate: z.number().min(0).nullable(),
  extra_hire_unit_deposit_rate: z.number().min(0),
  extra_hire_unit_supplier_net_rate: z.number().min(0).nullable(),
  booking_fee_amount: z.number().min(0),
  charge_per_move_amount: z.number().min(0).nullable(),
  holding_deposit_amount: z.number().min(0),
  distance_allowed: z.number().min(0).nullable(),
  extra_distance_allowed_per_extra_hire_unit: z.number().min(0).nullish(),
  inclusions: z.object({
    upsert: z.array(
      z
        .object({
          type: z.enum(RelocationInclusionType),
          description: z.string().nullable(),
          value: z.number().nullable(),
        })
        .refine(
          (v) => {
            if (v.type !== RelocationInclusionType.Charge) {
              return true;
            }

            //Make sure it's not null OR 0
            return Boolean(v.value);
          },
          {
            error: "Charge must be greater than 0",
            path: ["value"],
          },
        ),
    ),
    delete: z.array(z.string()),
  }),
});

// Advertising commissions do not allow a minimum hire that can only be met by
// buying extra hire units on top of the advertised relocation hire.
export const minimumHireRefine: [
  (schema: {
    hire_units_allowed: number;
    minimum_hire_units: number;
  }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (schema) => schema.minimum_hire_units <= (schema.hire_units_allowed ?? 0),
  {
    message: "Cannot be more than the relocation hire",
    path: ["minimum_hire_units"],
  },
];

export const fixedLatestDepartureRefine: [
  (schema: {
    fixed_latest_departure_date?: string | null;
    available_to_date: string;
  }) => boolean,
  { message: string; path: PropertyKey[] },
] = [
  (schema) => {
    if (!schema.fixed_latest_departure_date) {
      return true;
    }

    return schema.fixed_latest_departure_date <= schema.available_to_date;
  },
  {
    message:
      "Fixed latest departure date cannot be after the available to date",
    path: ["fixed_latest_departure_date"],
  },
];

export const relocationDetailsValidation = relocationFields
  .pick({
    available_from_date: true,
    available_to_date: true,
    departureOffice: true,
    deliveryOfficeIds: true,
    supplier: true,
    vehicle: true,
    supplierInsuranceOption: true,
    quantity: true,
    type: true,
    retail_rate: true,
    hire_unit_type: true,
    hire_units_allowed: true,
    minimum_hire_units: true,
    extra_hire_units_allowed: true,
    extra_hire_unit_rate: true,
    distance_allowed: true,
    inclusions: true,
  })
  .refine(...minimumHireRefine);

export const relocationPricingValidation = relocationFields.pick({
  hire_unit_rate: true,
  extra_hire_unit_rate: true,
  extra_hire_unit_deposit_rate: true,
  extra_hire_unit_supplier_net_rate: true,
});

export const relocationConfigurationValidation = relocationFields
  .pick({
    fixed_latest_departure_date: true,
    available_to_date: true,
  })
  .refine(...fixedLatestDepartureRefine);

export const relocationInclusionsValidation = relocationFields.pick({
  inclusions: true,
});

export const relocationFeesValidation = relocationFields.pick({
  booking_fee_amount: true,
  charge_per_move_amount: true,
  holding_deposit_amount: true,
});

export const relocationAcknowledgementsValidation = z.object({
  acknowledgements: z.object({
    upsert: z.array(
      z.object({
        name: z.string().min(1, "Name is required"),
        acknowledgement: z.string().min(1, "Acknowledgement is required"),
      }),
    ),
    delete: z.array(z.string()),
  }),
});

export const relocationValidation = relocationFields
  .refine(...minimumHireRefine)
  .refine(...fixedLatestDepartureRefine);
